import { useCallback, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import type { SceneStore } from '@os/doc';
import { ListItem } from '../../ui';
import { sectionsInReadingOrder } from '../export/plan';
import type { EditorController } from './controller';

const cache = new WeakMap<object, { key: string; items: { id: string; name: string }[] }>();

/** Sections of the board in reading order (top to bottom, then left to right); a click fits one in the view. */
export function SectionList({ store, controller }: { store: SceneStore; controller: Pick<EditorController, 'zoomToBox'> }) {
  const { t } = useTranslation();
  const subscribe = useCallback((cb: () => void) => store.subscribeList(cb), [store]);
  const items = useSyncExternalStore(subscribe, () => {
    const list = sectionsInReadingOrder(store).map(({ id, name }) => ({ id, name }));
    const key = JSON.stringify(list);
    const hit = cache.get(store);
    if (hit?.key === key) return hit.items;
    cache.set(store, { key, items: list });
    return list;
  });
  if (items.length === 0) return null;
  return (
    <nav aria-label={t('minimap.sections')} className="max-h-40 overflow-y-auto border-t border-border p-1">
      <ul>
        {items.map((s) => (
          <li key={s.id}>
            <ListItem
              label={s.name || t('editor.untitledSection')}
              onClick={() => {
                const w = store.graph().world.get(s.id);
                if (w) controller.zoomToBox(w);
              }}
              className="h-7"
            />
          </li>
        ))}
      </ul>
    </nav>
  );
}
